import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import css from 'styled-jsx/css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faCircleNotch,
} from '@fortawesome/free-solid-svg-icons';

import {
  XIRGO_V1_VENDOR_ID,
  XIRGO_V2_VENDOR_ID,
} from '../../Constants/Vendors';
import IconsContext from '../../Context/IconsContext';

const { className: modalClass, styles: modalStyles } = css.resolve`
  @media (max-width: 991px) {
    .modal-dialog {
      height: 100%;
      width: 100%;
      max-height: 100%;
      max-width: 100%;
      margin: 0;
    }
  }
`;

const { className: contentClass, styles: contentStyles } = css.resolve`
  @media (max-width: 767px) {
    .modal-content {
      display: grid;
      grid-template-rows: auto 60px;
    }
  }
  @media (max-width: 991px) {
    .modal-content {
      height: 100%;
    }
  }
  @media (min-width: 992px) {
    .modal-content {
      border-radius: 1rem;
    }
  }
`;

const { className: modalBodyClass, styles: modalBodyStyles } = css.resolve`
  .modal-body {
    /* padding: 0; */
    overflow: auto;
  }
`;

export default function NewCertificateModalDisplay(props) {
  const {
    asset: { vendor },
    certRequestPending,
    error,
    generateCertificate,
    handleDownloadAndClose,
    isOpen,
    toggleModal,
    toggleView,
    viewing,
  } = props;
  const { certificateIcon } = useContext(IconsContext);
  return (
    <Modal
      backdrop={certRequestPending ? 'static' : true}
      centered
      className={modalClass}
      contentClassName={contentClass}
      isOpen={isOpen}
      size="md"
      toggle={toggleModal}
    >
      <ModalBody className={`${modalBodyClass} container-fluid`}>
        <button
          className="btn btn-link position-absolute close-modal-x text-muted"
          disabled={certRequestPending}
          onClick={toggleModal}
        >
          &#x2A09;
        </button>
        <div className="row justify content-center">
          <img
            src={certificateIcon}
            alt="certificate icon"
            className="img-fluid mx-auto"
            style={{ maxHeight: '60px' }}
          />
        </div>
        <div className="row justify-content-center mt-3">
          <h5 className="modal-title text-text-dark">New Certificate</h5>
        </div>
        <div className="row justify-content-center mt-5 mb-5">
          <button
            className={
              viewing === 'create' ? 'btn modal-button-submit' : 'd-none'
            }
            onClick={generateCertificate}
          >
            {certRequestPending ? (
              <FontAwesomeIcon icon={faCircleNotch} className="mx-5" spin />
            ) : (
              'Generate New Certificate'
            )}
          </button>
          <FontAwesomeIcon
            icon={faCheckCircle}
            size="3x"
            className={viewing === 'confirm' ? 'text-success' : 'd-none'}
          />
        </div>
        <div className="row mx-3 justify-content-md-center justify-content-lg-start">
          <span
            className={
              viewing === 'create' && !['elevat'].includes(vendor)
                ? 'text-muted three-quarter-text'
                : 'd-none'
            }
          >
            Generating a new certificate will make the existing certificate
            invalid.
          </span>
          <span
            className={
              viewing === 'confirm' && !['elevat'].includes(vendor)
                ? 'text-muted three-quarter-text'
                : 'd-none'
            }
          >
            A new certificate has been generated for this gateway. If there was
            a problem, click back to generate another certificate.
          </span>
          <span
            className={
              viewing === 'confirm' && ['elevat'].includes(vendor)
                ? 'text-muted three-quarter-text'
                : 'd-none'
            }
          >
            A provisioning queue entry has been created to re-key this gateway.
            Please reboot the gateway so it sees the new key. The operation can
            be cancelled through the provisioning queue.
          </span>
        </div>
        <div className="row mt-1 mx-3 justify-content-md-center justify-content-lg-start">
          <span
            className={
              viewing === 'create' && ['elevat'].includes(vendor)
                ? 'text-muted three-quarter-text'
                : 'd-none'
            }
          >
            This gateway will be put into the provisioning queue. The
            certificate will be downloaded when the gateway next connects. The
            provisioning queue entry can be deleted to undo the new key action.
          </span>
        </div>
        <div className="row mt-1 mx-3 justify-content-md-center justify-content-lg-start">
          <span
            className={
              certRequestPending &&
              [XIRGO_V1_VENDOR_ID, XIRGO_V2_VENDOR_ID].includes(vendor)
                ? 'text-danger three-quarter-text'
                : 'd-none'
            }
          >
            This operation may take 10-20 seconds to complete. Please do not
            refresh your browser or naviagate away from this page until it
            completes.
          </span>
        </div>
        {error ? (
          <div className="row m-3 justify-content-md-center justify-content-lg-start">
            <span className="text-danger">{error}</span>
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter className="justify-content-start border-top-0 mt-lg-5">
        <button
          className={
            viewing === 'create'
              ? 'btn btn-outline-secondary px-4 py-0 three-quarter-text'
              : 'd-none'
          }
          disabled={certRequestPending}
          onClick={toggleModal}
        >
          Cancel
        </button>
        <button
          className={
            viewing === 'confirm'
              ? 'btn btn-outline-secondary px-4 py-0 three-quarter-text'
              : 'd-none'
          }
          onClick={toggleView}
        >
          Back
        </button>
        <button
          className={
            viewing === 'confirm' && !['elevat'].includes(vendor)
              ? 'btn modal-button-submit ml-auto px-4 py-0 three-quarter-text'
              : 'd-none'
          }
          onClick={handleDownloadAndClose}
        >
          Download &#38; Close
        </button>
        <button
          className={
            viewing === 'confirm' && ['elevat'].includes(vendor)
              ? 'btn modal-button-submit ml-auto px-4 py-0 three-quarter-text'
              : 'd-none'
          }
          onClick={toggleModal}
        >
          Close
        </button>
      </ModalFooter>
      {modalStyles}
      {contentStyles}
      {modalBodyStyles}
      <style jsx>{`
        .close-modal-x {
          right: 3px;
          top: 3px;
          font-size: 1rem;
          background-color: transparent;
          text-decoration: none;
          outline: none;
          cursor: pointer;
        }
      `}</style>
    </Modal>
  );
}

NewCertificateModalDisplay.propTypes = {
  asset: PropTypes.shape({
    vendor: PropTypes.oneOf([
      'elevat',
      '1',
      XIRGO_V1_VENDOR_ID,
      XIRGO_V2_VENDOR_ID,
    ]),
  }),
  certRequestPending: PropTypes.bool,
  error: PropTypes.string,
  generateCertificate: PropTypes.func,
  handleDownloadAndClose: PropTypes.func,
  isOpen: PropTypes.bool,
  toggleModal: PropTypes.func,
  toggleView: PropTypes.func,
  viewing: PropTypes.string,
};

NewCertificateModalDisplay.defaultProps = { asset: {} };
